// file imports
import { IEvent } from './index';

export type ContactFeedback = Record<string, string>;
export type SocialFeedback = Record<string, string>;
export type TextFeedback = string | { text: string };
export type BookingFeedback = { date: string; time: string };
export type RatingFeedback = number; // 1-5
export type BooleanFeedback = boolean;
export type OptionFeedback = string; // Selected option label
export type SkippedFeedback = null;
export type NpsFeedback = number; // 0-10 NPS scale
export type ScaleFeedback = string; // Custom scale options (e.g., "Very valuable", "Extremely valuable")
export type AnswerFeedback = string | number | boolean | null | ContactFeedback | SocialFeedback | TextFeedback | BookingFeedback;

export interface IFeedback {
  _id?: string;
  user?: {
    name: string;
    email: string;
    phoneNumber: string;
    comments: string;
  };
  eventType: 'Presentation' | 'Event';
  event: IEvent;
  status: 'pending' | 'approved' | 'rejected';
  consentToShare: boolean;
  consentToShowName: boolean;
  answers: {
    question: string;
    label: string;
    answer: AnswerFeedback;
  }[];
}

// Type guards for feedback answer validation
export const isContactFeedback = (answer: AnswerFeedback): answer is ContactFeedback => typeof answer === 'object' && answer !== null && !Array.isArray(answer) && !('text' in answer) && !('date' in answer);
export const isSocialFeedback = (answer: AnswerFeedback): answer is SocialFeedback => typeof answer === 'object' && answer !== null && !Array.isArray(answer) && !('text' in answer) && !('date' in answer);
export const isTextFeedback = (answer: AnswerFeedback): answer is TextFeedback => typeof answer === 'string' || (typeof answer === 'object' && answer !== null && 'text' in answer);
export const isBookingFeedback = (answer: AnswerFeedback): answer is BookingFeedback => typeof answer === 'object' && answer !== null && 'date' in answer && 'time' in answer;
export const isRatingFeedback = (answer: AnswerFeedback): answer is RatingFeedback => typeof answer === 'number' && answer >= 1 && answer <= 5;
export const isBooleanFeedback = (answer: AnswerFeedback): answer is BooleanFeedback => typeof answer === 'boolean';
export const isOptionFeedback = (answer: AnswerFeedback): answer is OptionFeedback => typeof answer === 'string';
export const isSkippedFeedback = (answer: AnswerFeedback): answer is SkippedFeedback => answer === null;
export const isNpsFeedback = (answer: AnswerFeedback): answer is NpsFeedback => typeof answer === 'number' && answer >= 0 && answer <= 10;
export const isScaleFeedback = (answer: AnswerFeedback): answer is ScaleFeedback => typeof answer === 'string' && answer !== '';
